import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Zap, DollarSign, ArrowRight, CheckCircle2, Shield, Eye, EyeOff, Network, Crown, Sparkles } from 'lucide-react';
import { trackGoogleAdsConversion } from '@/lib/google-ads';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AffiliateRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('convite');
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [inviteCompanyName, setInviteCompanyName] = useState('');
  const [form, setForm] = useState({
    name: '', email: '', password: '', whatsapp: '', cpf_cnpj: '', pix_key: ''
  });

  // 1-click invite: pre-fill form via token from email/whatsapp invite
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/affiliates/invite/${inviteToken}`);
        if (cancelled) return;
        if (data.already_affiliate) {
          toast.info('Você já tem uma conta de afiliado. Faça login.');
          navigate('/afiliados/login');
          return;
        }
        setForm(prev => ({
          ...prev,
          name: data.name || '',
          email: data.email || '',
          whatsapp: (data.whatsapp || '').replace(/\D/g, ''),
        }));
        setInviteCompanyName(data.company_name || '');
        setShowForm(true);
        toast.success('Bem-vindo! Seus dados já estão preenchidos. Crie uma senha e finalize.');
      } catch {
        toast.error('Convite inválido ou expirado.');
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Captura origem: cookie ref_partner + UTMs salvos
      const refPartnerCookie = document.cookie.split(';').find(c => c.trim().startsWith('ref_partner='));
      const referred_by_partner = refPartnerCookie ? refPartnerCookie.split('=')[1].trim() : undefined;

      let utm = {};
      try {
        const stored = sessionStorage.getItem('utm_params') || localStorage.getItem('utm_params');
        if (stored) utm = JSON.parse(stored);
      } catch { /* ignore */ }
      const referrer = document.referrer ? document.referrer.slice(0, 200) : undefined;

      await axios.post(`${API}/affiliates/register`, {
        ...form,
        referred_by_partner,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        utm_content: utm.utm_content,
        utm_term: utm.utm_term,
        utm_referrer: utm.utm_referrer || referrer,
      });
      setRegistered(true);
      toast.success('Cadastro realizado! Aguarde a aprovação.');
      trackGoogleAdsConversion('affiliate_signup');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no cadastro');
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (registered) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A2647] to-[#071A33] flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Cadastro Enviado!</h2>
            <p className="text-slate-600 mb-6">
              Seu cadastro está em análise. Você receberá acesso assim que for aprovado pelo administrador.
            </p>
            <Link to="/afiliados/login">
              <Button className="w-full bg-[#0A2647] hover:bg-[#0A2647]/90 text-white h-11">
                Já fui aprovado — Fazer Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A2647] to-[#071A33]">
      {/* Banner de convite (1-click) */}
      {inviteToken && inviteCompanyName && (
        <div className="bg-gradient-to-r from-[#C5A059] to-[#b8933f] text-[#0A2647]" data-testid="invite-banner">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-center gap-2 text-center">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm font-semibold">
              Convite especial para o dono do <strong>{inviteCompanyName}</strong>: seus dados já estão preenchidos abaixo
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="pt-8 pb-4 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/" className="inline-block mb-6">
            <span className="text-[#C5A059] font-bold text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>Falo Com O Dono</span>
          </Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ganhe Dinheiro Indicando<br />
            <span className="text-[#C5A059]">Empresas Inteligentes</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Comissões de até <strong className="text-[#C5A059]">R$ 250,00 por venda</strong> + bônus pelas vendas da sua rede. Pagamento via PIX, sem investimento e sem complicação.
          </p>
        </div>
      </header>

      {/* 3 Steps: Indique, Ative, Receba */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-0 bg-white/5 backdrop-blur-sm text-center group hover:bg-white/10 transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#C5A059]/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Users className="w-8 h-8 text-[#C5A059]" />
                </div>
                <div className="text-xs font-bold text-[#C5A059] mb-2 tracking-widest">PASSO 1</div>
                <h3 className="text-xl font-bold text-white mb-2">Indique</h3>
                <p className="text-sm text-slate-400">
                  Compartilhe seu link exclusivo com empresas que precisam de mais avaliações no Google.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 bg-white/5 backdrop-blur-sm text-center group hover:bg-white/10 transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#C5A059]/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Zap className="w-8 h-8 text-[#C5A059]" />
                </div>
                <div className="text-xs font-bold text-[#C5A059] mb-2 tracking-widest">PASSO 2</div>
                <h3 className="text-xl font-bold text-white mb-2">Ative</h3>
                <p className="text-sm text-slate-400">
                  A empresa se cadastra pelo seu link e assina um plano. Você é vinculado automaticamente.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 bg-white/5 backdrop-blur-sm text-center group hover:bg-white/10 transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <DollarSign className="w-8 h-8 text-emerald-400" />
                </div>
                <div className="text-xs font-bold text-emerald-400 mb-2 tracking-widest">PASSO 3</div>
                <h3 className="text-xl font-bold text-white mb-2">Receba</h3>
                <p className="text-sm text-slate-400">
                  Receba sua comissão via PIX em parcela única, mais bônus por cada venda feita pela sua rede.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Commission Explanation */}
      <section className="pb-8 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Tabela de Comissoes por Plano */}
          <Card className="border-0 bg-white/5 backdrop-blur-sm">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-white mb-1 text-center">Quanto você recebe por cada plano vendido</h3>
              <p className="text-xs text-slate-400 text-center mb-5">Comissão paga em parcela única via PIX assim que o cliente paga</p>

              <div className="space-y-2">
                <div className="flex items-center justify-between p-3.5 bg-white/5 rounded-xl border border-white/5 hover:border-[#C5A059]/30 transition">
                  <div>
                    <p className="text-sm font-bold text-white">Plano Básico</p>
                    <p className="text-[11px] text-slate-400">Mensalidade do cliente: R$ 29,90</p>
                  </div>
                  <span className="text-lg font-extrabold text-emerald-400" data-testid="comm-basico">R$ 50,00</span>
                </div>

                <div className="flex items-center justify-between p-3.5 bg-white/5 rounded-xl border border-white/5 hover:border-[#C5A059]/30 transition">
                  <div>
                    <p className="text-sm font-bold text-white">Plano Pro</p>
                    <p className="text-[11px] text-slate-400">Mensalidade do cliente: R$ 39,90</p>
                  </div>
                  <span className="text-lg font-extrabold text-emerald-400" data-testid="comm-pro">R$ 60,00</span>
                </div>

                <div className="flex items-center justify-between p-3.5 bg-white/5 rounded-xl border border-white/5 hover:border-[#C5A059]/30 transition">
                  <div>
                    <p className="text-sm font-bold text-white">Plano Empresarial</p>
                    <p className="text-[11px] text-slate-400">Mensalidade do cliente: R$ 59,90</p>
                  </div>
                  <span className="text-lg font-extrabold text-emerald-400" data-testid="comm-empresarial">R$ 100,00</span>
                </div>

                <div className="flex items-center justify-between p-3.5 bg-white/5 rounded-xl border border-white/5 hover:border-[#C5A059]/30 transition">
                  <div>
                    <p className="text-sm font-bold text-white">Plano Elite</p>
                    <p className="text-[11px] text-slate-400">Mensalidade do cliente: R$ 99,90</p>
                  </div>
                  <span className="text-lg font-extrabold text-emerald-400" data-testid="comm-elite">R$ 150,00</span>
                </div>

                <div className="flex items-center justify-between p-3.5 bg-[#C5A059]/10 rounded-xl border border-[#C5A059]/30">
                  <div>
                    <p className="text-sm font-bold text-white flex items-center gap-1.5">
                      <Crown className="w-4 h-4 text-[#C5A059]" /> Plano 5 Estrelas
                    </p>
                    <p className="text-[11px] text-[#C5A059]/80">Mensalidade do cliente: R$ 149,90</p>
                  </div>
                  <span className="text-lg font-extrabold text-[#C5A059]" data-testid="comm-five-stars">R$ 250,00</span>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <DollarSign className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <p className="text-[10px] text-slate-400">PIX em parcela única</p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <Zap className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <p className="text-[10px] text-slate-400">Pagamento automático</p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <Shield className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <p className="text-[10px] text-slate-400">Sem retenção, valor cheio</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ganhos da Rede - Efeito Multiplicador */}
          <Card className="border-0 bg-gradient-to-br from-[#C5A059]/15 to-[#C5A059]/5 backdrop-blur-sm border border-[#C5A059]/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Network className="w-5 h-5 text-[#C5A059]" />
                <h3 className="text-lg font-bold text-white">Sua venda continua trabalhando para você</h3>
              </div>
              <p className="text-sm text-slate-300 text-center mb-5 leading-relaxed">
                Toda empresa que você implantou exibe o nosso link na <strong className="text-white">página de agradecimento das avaliações</strong>. Quando outro dono de negócio vê e também assina o FaloComODono por esse link, <strong className="text-white">conta como mais uma venda sua</strong> — você recebe a comissão integral do plano que ele escolher.
              </p>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-xs font-bold text-[#C5A059] mb-3 tracking-widest text-center">EFEITO MULTIPLICADOR</p>
                <div className="space-y-2.5 text-sm text-slate-300">
                  <div className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-[#C5A059]/20 text-[#C5A059] text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                    <p>Você indica a <strong className="text-white">Padaria do João</strong> e ganha sua comissão (ex: Plano Pro = <strong className="text-emerald-400">R$ 60,00</strong>).</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-[#C5A059]/20 text-[#C5A059] text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                    <p>A Padaria recebe avaliações e nosso link aparece para os clientes finais dela na página de agradecimento.</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-[#C5A059]/20 text-[#C5A059] text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                    <p>O dono da <strong className="text-white">Pizzaria da esquina</strong> vê, clica e assina o plano 5 Estrelas. <strong className="text-emerald-400">+ R$ 250,00 cai no seu PIX</strong> — você ganha de novo, sem ter feito nada.</p>
                  </div>
                </div>
              </div>

              <p className="text-[12px] text-slate-300 text-center mt-4 leading-relaxed">
                Na prática: <strong className="text-white">cada empresa que você implanta vira um anúncio permanente do FaloComODono</strong> — e cada nova adesão por esse caminho é uma venda sua.
              </p>
            </CardContent>
          </Card>

          {/* Bonus de Lideranca - Apenas Master */}
          <Card className="border-0 bg-blue-500/5 backdrop-blur-sm border border-blue-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-blue-300" />
                <h3 className="text-base font-bold text-white">Bônus de Liderança (só para Afiliados Master)</h3>
              </div>
              <p className="text-xs text-slate-400 text-center mb-4 leading-relaxed">
                Se você é Afiliado Master e tem outros Afiliados na sua equipe, ganha um bônus extra a cada venda que <strong className="text-white">eles</strong> fizerem.
              </p>
              <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-300" />
                  <p className="text-sm text-white">Por cada venda do seu Afiliado</p>
                </div>
                <span className="text-base font-extrabold text-blue-300">+ R$ 50 a R$ 100</span>
              </div>
              <p className="text-[10px] text-blue-300/80 text-center mt-2">
                R$ 50 (Básico/Pro) · R$ 70 (Empresarial) · R$ 100 (Elite/5 Estrelas)
              </p>
              <p className="text-[10px] text-slate-500 text-center mt-3">
                * Disponível mediante aprovação como Afiliado Master pela equipe interna.
              </p>
            </CardContent>
          </Card>

          {/* Exemplo prático */}
          <Card className="border-0 bg-white/5 backdrop-blur-sm">
            <CardContent className="p-6">
              <p className="text-xs font-bold text-[#C5A059] mb-3 text-center tracking-widest">EXEMPLO PRÁTICO DE 1 MÊS</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-slate-400 text-[11px]">Você indicou</p>
                  <p className="text-white font-bold">3 empresas (Plano Pro)</p>
                  <p className="text-emerald-400 font-extrabold mt-1">R$ 180,00</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-slate-400 text-[11px]">Veio da página de uma delas</p>
                  <p className="text-white font-bold">+1 cliente Plano Elite</p>
                  <p className="text-emerald-400 font-extrabold mt-1">R$ 150,00</p>
                </div>
                <div className="bg-[#C5A059]/15 rounded-lg p-3 text-center border border-[#C5A059]/30">
                  <p className="text-[#C5A059] text-[11px] font-semibold">TOTAL NO PIX</p>
                  <p className="text-white font-bold">Você recebeu</p>
                  <p className="text-[#C5A059] font-extrabold text-lg mt-1">R$ 330,00</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA / Form */}
      <section className="pb-16 px-4">
        <div className="max-w-md mx-auto">
          {!showForm ? (
            <div className="text-center">
              <Button
                onClick={() => setShowForm(true)}
                className="bg-[#C5A059] hover:bg-[#C5A059]/90 text-[#0A2647] font-bold text-lg h-14 px-10 rounded-xl shadow-lg shadow-[#C5A059]/20"
                data-testid="btn-show-affiliate-form"
              >
                Quero Ser Afiliado <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="mt-4 text-slate-500 text-sm">
                Já tem conta? <Link to="/afiliados/login" className="text-[#C5A059] hover:underline">Fazer Login</Link>
              </p>
            </div>
          ) : (
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 text-center">Cadastro de Afiliado</h3>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Nome Completo</label>
                    <Input value={form.name} onChange={(e) => update('name', e.target.value)} required className="h-10" placeholder="Seu nome" data-testid="aff-name" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">E-mail</label>
                    <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required className="h-10" placeholder="seu@email.com" data-testid="aff-email" />
                  </div>
                  <div className="relative">
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Senha de Acesso</label>
                    <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => update('password', e.target.value)} required className="h-10 pr-10" placeholder="Crie uma senha" data-testid="aff-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[30px] text-slate-400">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">WhatsApp</label>
                    <Input value={form.whatsapp} onChange={(e) => update('whatsapp', e.target.value.replace(/\D/g, ''))} required className="h-10" placeholder="5531999999999" data-testid="aff-whatsapp" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Formato: 55 + DDD + número</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">CPF ou CNPJ</label>
                    <Input value={form.cpf_cnpj} onChange={(e) => update('cpf_cnpj', e.target.value)} required className="h-10" placeholder="000.000.000-00" data-testid="aff-cpf" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Chave PIX (para receber comissões)</label>
                    <Input value={form.pix_key} onChange={(e) => update('pix_key', e.target.value)} required className="h-10" placeholder="CPF, e-mail, telefone ou chave aleatória" data-testid="aff-pix" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-[#C5A059] hover:bg-[#C5A059]/90 text-[#0A2647] font-bold h-11 mt-2" data-testid="aff-submit">
                    {submitting ? 'Enviando...' : 'Enviar Cadastro'}
                  </Button>
                </form>
                <p className="text-[11px] text-slate-400 text-center mt-3">
                  Ao se cadastrar, você concorda com os termos do programa de afiliados.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
};

export default AffiliateRegister;
